import { orgCalendarDate, orgWallClock } from '@/lib/utils/custom-date/custom-date';

/** Minimal CSV export used by the Business Card and ID Card requests screens. */

/** Quotes a cell when it contains a delimiter, quote or line break (RFC 4180). */
const escapeCell = (value: string) =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * Phone numbers are wrapped as `="..."` so Excel keeps the leading `+`/zeros instead of turning
 * them into numbers -- the same trick the legacy export used.
 */
export const asExcelText = (value: string | undefined | null) => (value ? `="${value}"` : '');

/** Builds the CSV text: a leading `S.No` column, then one column per header. */
export const buildCsv = (headers: string[], rows: Record<string, string | undefined | null>[]) => {
  const lines = [['S.No', ...headers].map(escapeCell).join(',')];
  rows.forEach((row, index) => {
    lines.push(
      [String(index + 1), ...headers.map((header) => row[header] ?? '')].map(escapeCell).join(',')
    );
  });
  return lines.join('\r\n') + '\r\n';
};

/** Triggers a browser download; the BOM lets Excel read UTF-8 names correctly. */
export const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * A UTC moment -> `2026-09-21 16-05-09`, the legacy "Requested On" export format, on the wall clock of
 * the record's `Timezone` (the organization's zone for old rows without one).
 */
export const formatRequestedOn = (isoString?: string, timeZone?: string | null) => {
  if (!isoString) return '';
  if (Number.isNaN(new Date(isoString).getTime())) return '';
  const p = orgWallClock(isoString, timeZone || undefined);
  return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}-${pad(p.minute)}-${pad(p.second)}`;
};

/** Today's date ("YYYY-MM-DD") in the organization's zone, for export file names. */
export const exportDateStamp = (now: Date = new Date()) => orgCalendarDate(now.toISOString());
